import React, { useState, useEffect } from 'react';
import { FiMenu } from 'react-icons/fi';
import finalCongrats from './img/final-congrats.png';
import './ConceptToCharacteristic.styles.sass';

const ConceptToCharacteristic = ({ interactive_challenges }) => {
  // Limita sempre a 3 opções por desafio: a correta + 2 distratores (layout limpo, sem 3+1).
  const pickOptions = (options, correct) => {
    const list = Array.isArray(options) ? options : [];
    const correctOpt = list.filter(option => option === correct);
    const distractors = list.filter(option => option !== correct);
    return [...correctOpt, ...distractors].slice(0, 3);
  };

  const themes = interactive_challenges.map((challenge, index) => ({
    concept: { id: `concept_${index}`, name: challenge.key_element },
    educative_feedback: challenge.educative_feedback,
    characteristics: pickOptions(challenge.options, challenge.correct_answer).map((option, optionIndex) => ({
      id: `option_${index}_${optionIndex}`,
      text: option,
      correct: option === challenge.correct_answer
    }))
  }));

  const [currentThemeIndex, setCurrentThemeIndex] = useState(0);
  const [shuffledCharacteristics, setShuffledCharacteristics] = useState([]);
  const [shake, setShake] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  // Desktop (mouse/trackpad) usa arrasta-e-solta; touch continua no toque —
  // o drag nativo do HTML5 não dispara em telas de toque (iOS Safari).
  const [isFinePointer] = useState(() => window.matchMedia?.('(pointer: fine)').matches ?? false);
  const [correctlyDropped, setCorrectlyDropped] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [showFinalCongrats, setShowFinalCongrats] = useState(false);

  useEffect(() => {
    shuffleCharacteristics();
  }, [currentThemeIndex]);

  const shuffleCharacteristics = () => {
    const theme = themes[currentThemeIndex];
    setShuffledCharacteristics(shuffleArray([...theme.characteristics]));
  };

  // Touch: toque direto (múltipla escolha). Desktop: arrasta-e-solta do conceito
  // até a explicação; o clique segue como fallback (teclado/acessibilidade).
  const handleSelectCharacteristic = characteristic => {
    if (correctlyDropped) return;
    if (characteristic.correct) {
      setCorrectlyDropped(true);
      setShowCongrats(true);
    } else {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleNext = () => {
    setCorrectlyDropped(false);
    setShowCongrats(false);

    if (currentThemeIndex + 1 < themes.length) {
      setCurrentThemeIndex(prevIndex => prevIndex + 1);
    } else {
      setShowFinalCongrats(true);
    }
  };

  return (
    <div className="concept-to-characteristic-container">
      {!showFinalCongrats ? (
        <>
          <div className="instructions flex w-full justify-between rounded-xl border border-solid border-neutral-100 bg-white px-4 py-2.5 font-semibold text-primary-dark shadow-card">
            <span>{!showCongrats ? (isFinePointer ? 'Arraste o conceito até a explicação que combina' : 'Toque na explicação que combina com o conceito') : 'Parabéns'}</span>
            {/* Denominador é o nº de desafios (themes), não o de opções embaralhadas */}
            <span> {`${currentThemeIndex + 1}/${themes.length}`}</span>
          </div>
          <div className="concept-container">
            <div
              className={`concept-card ${shake ? 'shake' : ''} ${correctlyDropped ? 'hidden' : ''} ${isDragging ? 'dragging' : ''}`}
              draggable={isFinePointer && !correctlyDropped}
              onDragStart={event => {
                event.dataTransfer.effectAllowed = 'move';
                // Firefox exige dados no dataTransfer pra iniciar o drag
                event.dataTransfer.setData('text/plain', themes[currentThemeIndex].concept.id);
                setIsDragging(true);
              }}
              onDragEnd={() => {
                setIsDragging(false);
                setDragOverId(null);
              }}
            >
              <p className="mb-4">{themes[currentThemeIndex].concept.name}</p>
              <FiMenu className="concept-card-icon" />
            </div>
          </div>
          <div className="characteristics-container">
            {shuffledCharacteristics.map(characteristic => (
              <button
                key={characteristic.id}
                type="button"
                className={`characteristic-card ${correctlyDropped && characteristic.correct ? 'stacked' : ''} ${dragOverId === characteristic.id ? 'drag-over' : ''}`}
                disabled={correctlyDropped}
                onClick={() => handleSelectCharacteristic(characteristic)}
                onDragOver={event => {
                  if (!isDragging || correctlyDropped) return;
                  event.preventDefault(); // habilita o drop neste alvo
                  event.dataTransfer.dropEffect = 'move';
                }}
                onDragEnter={() => isDragging && setDragOverId(characteristic.id)}
                onDragLeave={() => setDragOverId(prev => (prev === characteristic.id ? null : prev))}
                onDrop={event => {
                  event.preventDefault();
                  setDragOverId(null);
                  setIsDragging(false);
                  handleSelectCharacteristic(characteristic);
                }}
              >
                {characteristic.text}
              </button>
            ))}
          </div>
          {showCongrats && (
            <>
              <div className="congrats-message">{themes[currentThemeIndex]?.educative_feedback}</div>
              <button
                onClick={handleNext}
                className="btn btn--primary btn--wide"
              >
                {currentThemeIndex + 1 === themes.length ? 'Finalizar' : 'Próximo Desafio'}
              </button>
            </>
          )}
        </>
      ) : (
        <div className="final-congrats-message">
          <img
            src={finalCongrats}
            style={{ width: 100, height: 100, marginTop: 40, marginBottom: 20 }}
          />
          <h2>Parabéns!</h2>
          <p className="mb-4">Você completou todos os desafios!</p>
        </div>
      )}
    </div>
  );
};

const shuffleArray = array => {
  return array.sort(() => Math.random() - 0.5);
};

export default ConceptToCharacteristic;
